// @flow
import * as React from 'react'
import {
  Drawer,
  Button,
  Row,
  Col,
  Select,
  Radio,
  Divider,
  Checkbox,
  DatePicker,
  Icon,
  Tag,
} from 'antd'
import moment from 'moment'
import { injectIntl } from 'react-intl'

import './index.css'

type Props = {
  setActiveFilters: ({ [string]: Array<string> }) => void,
  locations: Array<Filter>,
  tags: Array<Filter>,
  setLanguage: ('it' | 'en') => void,
  isGeolocationEnabled: boolean,
  intl: any,
}

type State = {
  datesDrawer: boolean,
  locationsDrawer: boolean,
  tagsDrawer: boolean,
  datesFilter: {
    from: object | null,
    to: object | null,
  },
  savedDatesFilter: boolean,
  locationsFilter: Array<string>,
  gpsLocationFilter: boolean,
  tagsFilter: Array<string>,
  myFeFilter: boolean,
  langFilter: 'it' | 'en',
  langDrawer: boolean,
}

class Filters extends React.Component<Props, State> {
  constructor(props) {
    super(props)

    const currentLang = localStorage.getItem('lang')

    this.state = {
      datesDrawer: false,
      locationsDrawer: false,
      tagsDrawer: false,
      datesFilter: {
        from: moment(),
        to: moment(),
      },
      savedDatesFilter: false,
      locationsFilter: [],
      gpsLocationFilter: false,
      tagsFilter: [],
      myFeFilter: false,
      langFilter: currentLang ? currentLang : 'it',
      langDrawer: false,
    }
  }

  toggleDrawer = (drawer: string, value: string | void): void => () => {
    this.setState({
      [`${drawer}Drawer`]: value ?? !this.state[`${drawer}Drawer`],
    })
  }

  onFiltersChange = (filter: string, value: string | void): void => {
    this.setState({
      [`${filter}Filter`]: value ?? {},
    })
  }

  onDateFromChange = (value: object): void => {
    const from = moment(value)

    this.setState(prevState => ({
      datesFilter: {
        from,
        to: !prevState.datesFilter.to.isSameOrAfter(from, 'day')
          ? moment(from).add(1, 'd')
          : prevState.datesFilter.to,
      },
    }))
  }

  onDateToChange = (value: object): void => {
    const to = moment(value)

    this.setState(prevState => ({
      datesFilter: {
        from: !to.isSameOrAfter(prevState.datesFilter.from, 'day')
          ? moment(to).subtract(1, 'd')
          : prevState.datesFilter.from,
        to,
      },
    }))
  }

  onBooleanChange = (filter: string) => (e: SyntheticEvent<T>): void => {
    const value = e.target.checked
    this.setState({
      [`${filter}Filter`]: value ?? false,
    })
  }

  applyFilters = (filter: string): void => () => {
    this.setState(
      {
        [`${filter}Drawer`]: false,
        savedDatesFilter: true,
      },
      this.props.setActiveFilters({
        [filter]: this.state[`${filter}Filter`],
      }),
    )
  }

  resetDatesFilters = () => {
    this.setState(
      {
        datesFilter: {
          from: moment(),
          to: moment(),
        },
        savedDatesFilter: false,
        datesDrawer: false,
      },
      this.props.setActiveFilters({
        dates: {
          from: null,
          to: null,
        },
      }),
    )
  }

  render() {
    const {
      datesDrawer,
      locationsDrawer,
      tagsDrawer,
      datesFilter,
      savedDatesFilter,
      locationsFilter,
      gpsLocationFilter,
      tagsFilter,
      myFeFilter,
      langFilter,
      langDrawer,
    } = this.state

    const { locations, tags, isGeolocationEnabled, intl } = this.props

    const datesError =
      datesFilter.from &&
      datesFilter.to &&
      datesFilter.to.isBefore(datesFilter.from, 'day')

    return (
      <div className="filters-wrapper">
        <Row type="flex" justify="space-around" align="middle">
          <Col span={3}>
            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                ghost={!savedDatesFilter}
                shape="circle"
                icon="calendar"
                onClick={this.toggleDrawer('dates')}
                title={intl.formatMessage({
                  id: 'filters.dates.title',
                  defaultMessage: 'Dates filters',
                })}
              />
              <Drawer
                title={intl.formatMessage({
                  id: 'filters.dates.title',
                  defaultMessage: 'Dates filters',
                })}
                placement="bottom"
                height="auto"
                onClose={this.toggleDrawer('dates', false)}
                visible={datesDrawer}
                className="filters-drawer"
              >
                <label htmlFor="dates-filter-picker-from">
                  {intl.formatMessage({
                    id: 'filters.dates.from',
                    defaultMessage: 'From',
                  })}
                </label>
                <DatePicker
                  format={intl.locale === 'en' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}
                  id="dates-filter-picker-from"
                  onChange={this.onDateFromChange}
                  value={datesFilter.from}
                  className={datesError ? 'dates-error' : ''}
                  allowClear={false}
                  suffixIcon={datesError ? <Icon type="warning" /> : undefined}
                />
                <Divider style={{ height: 0 }} />
                <label htmlFor="dates-filter-picker-to">
                  {intl.formatMessage({
                    id: 'filters.dates.to',
                    defaultMessage: 'to',
                  })}
                </label>
                <DatePicker
                  id="dates-filter-picker-to"
                  format={intl.locale === 'en' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}
                  onChange={this.onDateToChange}
                  value={datesFilter.to}
                  className={datesError ? 'dates-error' : ''}
                  allowClear={false}
                  suffixIcon={datesError ? <Icon type="warning" /> : undefined}
                />
                <Divider />
                {datesError && (
                  <div>
                    <Tag color="gold" style={{ marginLeft: '1rem' }}>
                      {intl.formatMessage({
                        id: 'filters.dates.error',
                        defaultMessage: 'Incorrect date range values',
                      })}
                    </Tag>
                  </div>
                )}
                <Button
                  type="primary"
                  onClick={this.applyFilters('dates')}
                  disabled={datesError}
                >
                  {intl.formatMessage({
                    id: 'filters.apply',
                    defaultMessage: 'Apply filters',
                  })}
                </Button>
                {savedDatesFilter && (
                  <Button
                    type="ghost"
                    onClick={this.resetDatesFilters}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    {intl.formatMessage({
                      id: 'filters.reset',
                      defaultMessage: 'Reset filters',
                    })}
                  </Button>
                )}
              </Drawer>
            </div>
          </Col>
          <Col span={3}>
            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                ghost={
                  Object.values(locationsFilter).length === 0 &&
                  !gpsLocationFilter
                }
                shape="circle"
                icon="environment"
                onClick={this.toggleDrawer('locations')}
                title={intl.formatMessage({
                  id: 'filters.location.title',
                  defaultMessage: 'Filter by location',
                })}
              />
              <Drawer
                title={intl.formatMessage({
                  id: 'filters.location.title',
                  defaultMessage: 'Filter by location',
                })}
                placement="bottom"
                height="auto"
                onClose={this.applyFilters('locations')}
                visible={locationsDrawer}
                className="filters-drawer"
              >
                <label htmlFor="location-filter-select" className="sr-only">
                  {intl.formatMessage({
                    id: 'filters.location.title',
                    defaultMessage: 'Filter by location',
                  })}
                </label>
                <Checkbox
                  onChange={this.onBooleanChange('gpsLocation')}
                  disabled={!isGeolocationEnabled}
                  checked={gpsLocationFilter}
                >
                  {intl.formatMessage({
                    id: 'filters.geolocation',
                    defaultMessage: 'Near me',
                  })}
                  <span
                    style={{
                      fontSize: '0.8em',
                      padding: '0.5rem 0 0 0',
                      display: 'inline-block',
                      width: '100%',
                    }}
                  >
                    {intl.formatMessage({
                      id: 'filters.geolocation.description',
                      defaultMessage: 'Search within 10km nearby',
                    })}
                  </span>
                </Checkbox>
                {locations.length > 0 && (
                  <React.Fragment>
                    <Divider />
                    <Select
                      id="location-filter-select"
                      style={{ width: '100%' }}
                      showSearch
                      showArrow
                      allowClear
                      disabled={gpsLocationFilter}
                      mode="multiple"
                      placeholder={intl.formatMessage({
                        id: 'filters.location.selectplaceholder',
                        defaultMessage: 'Select a location',
                      })}
                      optionFilterProp="children"
                      onChange={value =>
                        this.onFiltersChange('locations', value)
                      }
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {locations.map(loc => (
                        <Select.Option value={loc.value} key={loc.value}>
                          {loc.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </React.Fragment>
                )}
                <Divider />
                <Button
                  type="primary"
                  onClick={() => {
                    this.applyFilters('locations')()
                    this.applyFilters('gpsLocation')()
                  }}
                >
                  {intl.formatMessage({
                    id: 'filters.apply',
                    defaultMessage: 'Apply filters',
                  })}
                </Button>
              </Drawer>
            </div>
          </Col>
          <Col span={3}>
            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                ghost={Object.values(tagsFilter).length === 0 && !myFeFilter}
                shape="circle"
                icon="tags"
                onClick={this.toggleDrawer('tags')}
                title={intl.formatMessage({
                  id: 'filters.tags.title',
                  defaultMessage: 'Filter by tag',
                })}
              />
              <Drawer
                title={intl.formatMessage({
                  id: 'filters.tags.title',
                  defaultMessage: 'Filter by tag',
                })}
                placement="bottom"
                height="auto"
                onClose={this.applyFilters('tags')}
                visible={tagsDrawer}
                className="filters-drawer"
              >
                <label htmlFor="tags-filter-select" className="sr-only">
                  {intl.formatMessage({
                    id: 'filters.tags.title',
                    defaultMessage: 'Filter by tag',
                  })}
                </label>
                <Checkbox
                  onChange={this.onBooleanChange('myFe')}
                  checked={myFeFilter}
                >
                  {intl.formatMessage({
                    id: 'filters.tags.myfe',
                    defaultMessage: 'In agreement with MyFE Card',
                  })}
                </Checkbox>
                <Divider />
                {tags.length > 0 && (
                  <Select
                    id="tags-filter-select"
                    style={{ width: '100%' }}
                    showSearch
                    showArrow
                    allowClear
                    mode="multiple"
                    placeholder={intl.formatMessage({
                      id: 'filters.tags.selectplaceholder',
                      defaultMessage: 'Select a category',
                    })}
                    optionFilterProp="children"
                    onChange={value => this.onFiltersChange('tags', value)}
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {tags.map(tag => (
                      <Select.Option value={tag.value} key={tag.value}>
                        {tag.label}
                      </Select.Option>
                    ))}
                  </Select>
                )}
                <Divider />
                <Button
                  type="primary"
                  onClick={() => {
                    this.applyFilters('tags')()
                    this.applyFilters('myFe')()
                  }}
                >
                  {intl.formatMessage({
                    id: 'filters.apply',
                    defaultMessage: 'Apply filters',
                  })}
                </Button>
              </Drawer>
            </div>
          </Col>
          <Col span={3}>
            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                shape="circle"
                icon="global"
                ghost
                onClick={this.toggleDrawer('lang')}
                title={intl.formatMessage({
                  id: 'filters.language.title',
                  defaultMessage: 'Language',
                })}
              />
              <Drawer
                title={intl.formatMessage({
                  id: 'filters.language.title',
                  defaultMessage: 'Language',
                })}
                placement="bottom"
                height="auto"
                onClose={this.applyFilters('lang')}
                visible={langDrawer}
                className="filters-drawer"
              >
                <label htmlFor="lang-filter-radio" className="sr-only">
                  {intl.formatMessage({
                    id: 'filters.language.title',
                    defaultMessage: 'Language',
                  })}
                </label>
                <div style={{ marginTop: 16 }}>
                  <Radio.Group
                    id="lang-filter-radio"
                    value={langFilter}
                    buttonStyle="solid"
                    onChange={e => this.onFiltersChange('lang', e.target.value)}
                  >
                    <Radio.Button
                      style={{
                        display: 'inline-block',
                        textTransform: 'uppercase',
                        paddingLeft: '1.5rem',
                        paddingRight: '1.5rem',
                      }}
                      value="it"
                    >
                      it
                    </Radio.Button>
                    <Radio.Button
                      style={{
                        display: 'inline-block',
                        textTransform: 'uppercase',
                        paddingLeft: '1.5rem',
                        paddingRight: '1.5rem',
                      }}
                      value="en"
                    >
                      en
                    </Radio.Button>
                  </Radio.Group>
                </div>
                <Divider />
                <Button type="primary" onClick={this.applyFilters('lang')}>
                  {intl.formatMessage({
                    id: 'filters.save',
                    defaultMessage: 'Save',
                  })}
                </Button>
              </Drawer>
            </div>
          </Col>
        </Row>
      </div>
    )
  }
}

export default React.memo<Props>(injectIntl(Filters))
